/**
 * Shared Snackbar + Alert styling aligned with the service inquiry confirmation toast
 * (`components/common/InquirySentToast/InquirySentToast.js`).
 */

/** @type {import('@mui/material').SnackbarProps['sx']} */
export const nkyAppSnackbarSx = {
  top: { xs: 16, sm: 24 },
  '& .MuiPaper-root': {
    width: '100%',
    maxWidth: { xs: 'calc(100vw - 24px)', sm: 480 },
    boxSizing: 'border-box',
  },
};

const baseAlertSx = {
  width: '100%',
  alignItems: 'flex-start',
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  borderRadius: '12px',
  border: '1px solid rgba(255,255,255,0.35)',
  color: '#fff',
  '& .MuiAlert-icon': {
    color: '#fff',
    paddingTop: '2px',
  },
  '& .MuiAlert-message': {
    width: '100%',
    minWidth: 0,
    paddingTop: '2px',
    paddingBottom: 0,
  },
  '& .MuiAlert-action': {
    paddingTop: '4px',
    marginRight: '-8px',
    alignItems: 'flex-start',
  },
};

/** Gradient fills per severity — same card treatment as inquiry confirmation (success). */
const severityLayer = {
  success: {
    background:
      'linear-gradient(135deg, #0B3FD4 0%, #0D4EFE 42%, #01C9A0 100%)',
    boxShadow:
      '0 4px 6px rgba(13, 78, 254, 0.12), 0 12px 28px rgba(13, 78, 254, 0.22)',
  },
  error: {
    background:
      'linear-gradient(135deg, #8B1515 0%, #D32F2F 42%, #EF5350 100%)',
    boxShadow:
      '0 4px 6px rgba(211, 47, 47, 0.14), 0 12px 28px rgba(211, 47, 47, 0.22)',
  },
  warning: {
    background:
      'linear-gradient(135deg, #BF360C 0%, #ED6C02 42%, #FFA726 100%)',
    boxShadow:
      '0 4px 6px rgba(237, 108, 2, 0.14), 0 12px 28px rgba(237, 108, 2, 0.2)',
  },
  info: {
    background:
      'linear-gradient(135deg, #01579B 0%, #0288D1 42%, #4FC3F7 100%)',
    boxShadow:
      '0 4px 6px rgba(2, 136, 209, 0.14), 0 12px 28px rgba(2, 136, 209, 0.2)',
  },
};

/**
 * @param {'success' | 'info' | 'warning' | 'error'} [severity]
 * @returns {import('@mui/material').AlertProps['sx']}
 */
export function getNkyAppAlertSx(severity = 'success') {
  const key =
    severity === 'error' ||
    severity === 'warning' ||
    severity === 'info' ||
    severity === 'success'
      ? severity
      : 'success';
  return {
    ...baseAlertSx,
    ...severityLayer[key],
  };
}

/**
 * Snackbar `onClose` handler that ignores `clickaway` (matches inquiry toast behavior).
 * @param {() => void} close
 */
export function nkySnackbarCloseIgnoringClickaway(close) {
  return function nkySnackbarOnClose(_event, reason) {
    if (reason === 'clickaway') return;
    close();
  };
}
